/**
 * useAutocompleteService Hook (Programmatic API - Not Legacy)
 *
 * Custom React hook for Google Places Autocomplete using AutocompleteService.
 * This is the programmatic API that provides predictions data without UI components.
 *
 * Benefits over Legacy Autocomplete:
 * - Not deprecated (modern API with long-term support)
 * - Full control over UI/UX
 * - Same pricing with session tokens
 * - Complete styling freedom
 *
 * @module useAutocompleteService
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { DEBUG_MODE } from '../constants/googlePlaces';
import logger from '../utils/logger';

/**
 * Custom hook for Google Places AutocompleteService integration
 *
 * @param {Object} config - Configuration object
 * @param {string} config.input - Search input value
 * @param {number} [config.debounceMs=300] - Debounce delay in milliseconds
 * @param {boolean} [config.enabled=true] - Enable/disable the hook
 * @param {Object} [config.options={}] - Additional AutocompleteService options
 *
 * @returns {Object} Hook state and methods
 * @returns {Array} suggestions - Array of place predictions
 * @returns {boolean} loading - Whether predictions are being fetched
 * @returns {string|null} error - Error status if request failed
 * @returns {Function} getPlaceDetails - Function to get full place details by place_id
 * @returns {Function} clearSuggestions - Function to manually clear suggestions
 *
 * @example
 * const { suggestions, loading, getPlaceDetails } = useAutocompleteService({
 *   input: searchText,
 *   debounceMs: 300,
 *   enabled: true
 * });
 */
export const useAutocompleteService = ({
  input,
  debounceMs = 300,
  enabled = true,
  countryCode = 'us',
  options = {}
}) => {
  // State
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Refs for Google services
  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const geocoderRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const previousInputRef = useRef(''); // Track previous input to prevent duplicate searches
  const initAttemptsRef = useRef(0);
  const maxInitAttempts = 20;
  const countryCodeRef = useRef(countryCode); // Track country changes

  // Memoize options to prevent fetchPredictions from changing on every render
  const stableOptions = useMemo(() => options, [JSON.stringify(options)]);

  /**
   * Initialize Google Places services
   */
  const initializeServices = useCallback(() => {
    if (!enabled) return;

    // Check if Google Maps API is loaded
    if (typeof window.google === 'undefined' || !window.google.maps || !window.google.maps.places) {
      initAttemptsRef.current += 1;

      if (initAttemptsRef.current > maxInitAttempts) {
        console.error('[useAutocompleteService] ❌ Google Maps API failed to load after maximum retries');
        setError('GOOGLE_MAPS_NOT_LOADED');
        return;
      }

      if (DEBUG_MODE) {
        logger.warn(
          `[useAutocompleteService] ⚠️ Google Maps API not loaded yet, retrying... (${initAttemptsRef.current}/${maxInitAttempts})`
        );
      }

      setTimeout(initializeServices, 200);
      return;
    }

    // Initialize AutocompleteService (provides predictions)
    if (!autocompleteServiceRef.current) {
      autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();

      if (DEBUG_MODE) {
        logger.debug('[useAutocompleteService] ✅ AutocompleteService initialized');
      }
    }

    // Initialize PlacesService (provides place details)
    if (!placesServiceRef.current) {
      // Create a dummy div for PlacesService (required by API)
      const dummyDiv = document.createElement('div');
      placesServiceRef.current = new window.google.maps.places.PlacesService(dummyDiv);

      if (DEBUG_MODE) {
        logger.debug('[useAutocompleteService] ✅ PlacesService initialized');
      }
    }

    // Initialize Geocoder (for reverse geocoding to get missing ZIP codes)
    if (!geocoderRef.current) {
      geocoderRef.current = new window.google.maps.Geocoder();

      if (DEBUG_MODE) {
        logger.debug('[useAutocompleteService] ✅ Geocoder initialized');
      }
    }

    // Create session token for cost optimization
    if (!sessionTokenRef.current) {
      sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();

      if (DEBUG_MODE) {
        logger.debug('[useAutocompleteService] 🎫 Session token created');
      }
    }
  }, [enabled]);

  // Initialize services on mount
  useEffect(() => {
    initializeServices();
  }, [initializeServices]);

  /**
   * Fetch autocomplete predictions
   */
  const fetchPredictions = useCallback((searchInput) => {
    if (!autocompleteServiceRef.current) {
      logger.warn('[useAutocompleteService] ⚠️ AutocompleteService not initialized');
      return;
    }

    if (!searchInput || searchInput.trim().length < 3) {
      setSuggestions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const requestOptions = {
      input: searchInput,
      sessionToken: sessionTokenRef.current,
      componentRestrictions: { country: countryCode.toLowerCase() },
      types: ['address'],
      ...stableOptions
    };

    if (DEBUG_MODE) {
      logger.debug('[useAutocompleteService] 🔍 Fetching predictions for:', searchInput);
    }

    autocompleteServiceRef.current.getPlacePredictions(
      requestOptions,
      (predictions, status) => {
        setLoading(false);

        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          setSuggestions(predictions);

          if (DEBUG_MODE) {
            logger.debug('[useAutocompleteService] ✅ Got', predictions.length, 'predictions');
          }
        } else if (status === window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
          setSuggestions([]);

          if (DEBUG_MODE) {
            logger.debug('[useAutocompleteService] 📭 No results found');
          }
        } else {
          setSuggestions([]);
          setError(status);

          if (DEBUG_MODE) {
            logger.warn('[useAutocompleteService] ⚠️ Error fetching predictions:', status);
          }
        }
      }
    );
  }, [stableOptions, countryCode]);

  /**
   * Clear suggestions when country changes
   * Prevents showing stale results from previous country
   */
  useEffect(() => {
    if (countryCodeRef.current !== countryCode) {
      countryCodeRef.current = countryCode;
      setSuggestions([]);
      previousInputRef.current = ''; // Reset to allow re-search with new country
    }
  }, [countryCode]);

  /**
   * Debounced search effect
   */
  useEffect(() => {
    if (!enabled || !input) {
      setSuggestions([]);
      setLoading(false);
      // ⚠️ DON'T reset previousInputRef here - this breaks duplicate detection
      // when input temporarily becomes empty during address selection
      // previousInputRef.current will be updated when actual search happens
      return;
    }

    // ✅ Prevent duplicate searches: Only search if input actually changed
    if (input === previousInputRef.current) {
      if (DEBUG_MODE) {
        logger.debug('[useAutocompleteService] ⏭️ Skipping search - input unchanged:', input);
      }
      return;
    }

    // Clear previous timer
    clearTimeout(debounceTimerRef.current);

    // Set new timer
    debounceTimerRef.current = setTimeout(() => {
      previousInputRef.current = input; // Update previous value before search
      fetchPredictions(input);
    }, debounceMs);

    // Cleanup
    return () => {
      clearTimeout(debounceTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input, enabled, debounceMs]); // fetchPredictions is intentionally omitted to avoid infinite loop

  /**
   * Get full place details by place_id
   * This terminates the session and triggers Place Details API call
   *
   * @param {string} placeId - The place_id from prediction
   * @param {Function} callback - Callback with (place, error)
   */
  const getPlaceDetails = useCallback((placeId, callback) => {
    if (!placesServiceRef.current) {
      console.error('[useAutocompleteService] ❌ PlacesService not initialized');
      callback(null, 'SERVICE_NOT_INITIALIZED');
      return;
    }

    if (DEBUG_MODE) {
      logger.debug('[useAutocompleteService] 📍 Fetching place details for:', placeId);
    }

    const request = {
      placeId: placeId,
      fields: ['address_components', 'formatted_address', 'geometry'],
      sessionToken: sessionTokenRef.current
    };

    placesServiceRef.current.getDetails(request, (place, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK) {
        if (DEBUG_MODE) {
          logger.debug('[useAutocompleteService] ✅ Got place details:', place);
        }

        // Reset session token after successful selection (start new session)
        sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();

        if (DEBUG_MODE) {
          logger.debug('[useAutocompleteService] 🔄 Session token reset for new search');
        }

        callback(place, null);
      } else {
        console.error('[useAutocompleteService] ❌ Error fetching place details:', status);
        callback(null, status);
      }
    });
  }, []);

  /**
   * Get missing ZIP code using Geocoding API (Reverse Geocoding)
   * Falls back to geometry location when address_components don't include postal_code
   *
   * Cost: $0.005 per request (cheaper than Places Nearby Search at $0.032)
   *
   * @param {Object} location - Google Maps LatLng object {lat(), lng()}
   * @param {Function} callback - Callback with (zipCode, error)
   */
  const getZipCodeFromLocation = useCallback((location, callback) => {
    if (!geocoderRef.current) {
      console.error('[useAutocompleteService] ❌ Geocoder not initialized');
      callback(null, 'GEOCODER_NOT_INITIALIZED');
      return;
    }

    if (!location || !location.lat || !location.lng) {
      console.error('[useAutocompleteService] ❌ Invalid location for ZIP lookup');
      callback(null, 'INVALID_LOCATION');
      return;
    }

    if (DEBUG_MODE) {
      logger.debug('[useAutocompleteService] 🔄 Reverse geocoding to find ZIP at:', location);
    }

    // Create LatLng object for Geocoder
    const latLng = new window.google.maps.LatLng(location.lat(), location.lng());

    // Reverse geocode to find postal_code
    geocoderRef.current.geocode({ location: latLng }, (results, status) => {
      if (status === 'OK' && results && results.length > 0) {
        if (DEBUG_MODE) {
          logger.debug('[useAutocompleteService] ✅ Reverse geocoding results:', results);
        }

        // Search through all results for postal_code component
        for (const result of results) {
          const postalCodeComponent = result.address_components?.find((component) =>
            component.types.includes('postal_code')
          );

          if (postalCodeComponent) {
            const zipCode = postalCodeComponent.long_name || postalCodeComponent.short_name;

            if (DEBUG_MODE) {
              logger.debug('[useAutocompleteService] ✅ Found ZIP via reverse geocoding:', zipCode);
            }

            callback(zipCode, null);
            return;
          }
        }

        // No postal code found in any result
        if (DEBUG_MODE) {
          logger.warn('[useAutocompleteService] ⚠️ No postal code found in reverse geocoding results');
        }
        callback(null, 'NO_POSTAL_CODE_FOUND');
      } else {
        console.error('[useAutocompleteService] ❌ Reverse geocoding failed:', status);
        callback(null, status);
      }
    });
  }, []);

  /**
   * Manually clear suggestions
   */
  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
    setLoading(false);
    setError(null);
  }, []);

  /**
   * Manually update previousInputRef (called after place selection)
   * This prevents duplicate searches when component re-renders after address selection
   *
   * @param {string} value - The value to set as previous input
   */
  const updatePreviousInput = useCallback((value) => {
    previousInputRef.current = value;
  }, []);

  return {
    suggestions,
    loading,
    error,
    getPlaceDetails,
    getZipCodeFromLocation,
    clearSuggestions,
    updatePreviousInput  // ← NEW: Manually sync previousInputRef after selection
  };
};

export default useAutocompleteService;
